/**
 * Modal d'export Excel comparatif avant/après rachat.
 *
 * Couvre :
 *   - Rachats batch (via RachatApplique snapshots)
 *   - Modifications manuelles (via HistoriqueModificationNote/Examen)
 *   - Étudiants non modifiés (pour vue d'ensemble complète)
 *
 * Stats incluses :
 *   - Rachetés via batch
 *   - Modifiés manuellement seulement
 *   - Catégorie avant : sous admission (8-9.99), éliminatoire (>=10 mais ajourné)
 *   - Transitions de statut (admis ↔ ajourné)
 *
 * 100 % READ-ONLY côté serveur.
 */
import React, { useState } from 'react';
import { X, Download, Loader, Info } from 'lucide-react';
import apiClient from '../../config/api.config';

interface Props {
  open: boolean;
  onClose: () => void;
}

const POLES = ['Commerce', 'Juridique', 'Polytechnique'];
const NIVEAUX = ['L1', 'L2', 'L3'];

const ExportComparaisonRachatsModal: React.FC<Props> = ({ open, onClose }) => {
  const today = new Date().toISOString().slice(0, 10);
  // Par défaut : 30 jours avant aujourd'hui
  const defaultDateMin = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();

  const [poles, setPoles] = useState<string[]>([...POLES]);
  const [niveaux, setNiveaux] = useState<string[]>([...NIVEAUX]);
  const [semestre, setSemestre] = useState<'S1' | 'S2' | 'all'>('S1');
  const [dateMin, setDateMin] = useState(defaultDateMin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const togglePole = (p: string) => {
    setPoles((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };
  const toggleNiveau = (n: string) => {
    setNiveaux((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]));
  };

  const handleDownload = async () => {
    setError(null);
    if (poles.length === 0 || niveaux.length === 0) {
      setError('Sélectionnez au moins un pôle et un niveau.');
      return;
    }
    setLoading(true);
    try {
      const resp = await apiClient.get('/rachat-intelligent/export-comparaison/', {
        params: {
          poles: poles.join(','),
          niveaux: niveaux.join(','),
          semestre,
          date_min: dateMin,
        },
        responseType: 'blob',
      });
      if (resp.status === 204) {
        setError('Aucune donnée correspondante au scope choisi.');
        return;
      }
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disp = resp.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(disp);
      a.download = m ? m[1] : `comparaison_rachats_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Erreur lors du téléchargement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Download size={20} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Export comparaison avant/après rachat
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm text-blue-900 dark:text-blue-100">
            <Info size={16} className="flex-shrink-0 mt-0.5" />
            <span>
              Export <strong>lecture seule</strong>. Inclut tous les étudiants du scope (rachetés via batch,
              modifiés manuellement, ou inchangés). 2 onglets : <strong>Comparaison</strong> + <strong>Statistiques</strong>.
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Pôles
            </label>
            <div className="flex flex-wrap gap-2">
              {POLES.map((p) => (
                <label
                  key={p}
                  className={`flex items-center gap-2 px-3 py-1.5 border rounded cursor-pointer transition ${
                    poles.includes(p)
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400 text-blue-900 dark:text-blue-100'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={poles.includes(p)}
                    onChange={() => togglePole(p)}
                    className="rounded"
                  />
                  {p}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Niveaux
            </label>
            <div className="flex flex-wrap gap-2">
              {NIVEAUX.map((n) => (
                <label
                  key={n}
                  className={`flex items-center gap-2 px-3 py-1.5 border rounded cursor-pointer transition ${
                    niveaux.includes(n)
                      ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400 text-blue-900 dark:text-blue-100'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={niveaux.includes(n)}
                    onChange={() => toggleNiveau(n)}
                    className="rounded"
                  />
                  {n}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Semestre
              </label>
              <select
                value={semestre}
                onChange={(e) => setSemestre(e.target.value as 'S1' | 'S2' | 'all')}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value="S1">S1 uniquement</option>
                <option value="S2">S2 uniquement</option>
                <option value="all">S1 + S2</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date min (rachats/modifs à partir de)
              </label>
              <input
                type="date"
                value={dateMin}
                max={today}
                onChange={(e) => setDateMin(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-900 dark:text-red-100">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Annuler
          </button>
          <button
            onClick={handleDownload}
            disabled={loading || poles.length === 0 || niveaux.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded"
          >
            {loading ? (
              <>
                <Loader size={16} className="animate-spin" /> Génération…
              </>
            ) : (
              <>
                <Download size={16} /> Télécharger Excel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportComparaisonRachatsModal;
